//======================================================================================================
//
// This store controls the layout inside of the visualizer stage.  It will control and have a direct link to the hierarchy and paraview view RPCs
//
//======================================================================================================

import { defineStore } from "pinia";
import {
	AddElementToRow,
	AddElementToStack,
	CleanLayout,
	CollapsePanel,
	FindNode,
	GetNewId,
	RemoveElementFromChildren,
	RetrieveElementFromLayout,
	RetrieveParentElementFromLayout,
	UpdateFlex
} from "../utils/PanelHelpers";
import { useActiveStore as active } from "./ssaStore/active";
import { useDisplayPaneStore } from "./ssaStore/displayPane";
import { usePickingStore } from "./ssaStore/picking";
import { usePvwStore as pvw } from "./ssaStore/pvw";

const initPanelState: LayoutType = {
	id: 0,
	component: "",
	type: "",
	componentState: {
		flex: 1,
		lastFlex: 1,
		color: ""
	},
	componentName: "",
	children: []
};

export const usePanelStore = defineStore("panelStore", {
	state: () =>
		({
			panelLayout: initPanelState,
			visualizers: [],
			activeVisualizer: null,
			ready: false
		}) as any,
	getters: {
		PANELS_READY(): boolean {
			return this.ready;
		},
		PANEL_LAYOUT(): any {
			return this.panelLayout;
		}
	},
	actions: {
		SET_READY(val: boolean) {
			this.ready = val;
		},
		SET_ACTIVE_VISUALIZER(id: number, viewId: number) {
			this.activeVisualizer = id;
			usePickingStore().SHOW_PICK_DATA(false);
			active().SSA_ACTIVE_VIEW_SET(viewId);

			if (
				!active().SSA_ACTIVE_DATASET?.id ||
				!useDisplayPaneStore().DISPLAY_STATES[viewId][active().SSA_ACTIVE_DATASET.id]
			)
				return; // without a dataset active we can't setup the display pane for a dataset
			const activeLayer = useDisplayPaneStore().DISPLAY_STATES[viewId][
				active().SSA_ACTIVE_DATASET.id
			].find((layer: any) => {
				return layer.selected && layer.open;
			});

			if (activeLayer) {
				active().SSA_ACTIVE_LAYER_SET(activeLayer.layerName);
				const layerName = activeLayer.layerName;
				let fieldList: any = ["hole_name", "bench_name", "pattern_name"];
				if (
					activeLayer.configuration.length > 0 &&
					activeLayer.configuration[0].availableProperties
				) {
					fieldList = fieldList.concat(
						activeLayer.configuration[0].availableProperties[0].children.map(
							(item: any) => {
								return item.value;
							}
						)
					);
				}
				if (
					!activeLayer.name.toLowerCase().includes("survey") &&
					!activeLayer.name.toLowerCase().includes("slic")
				) {
					pvw().PVW_SET_FIELD_LIST({ layerName, viewId, fieldList });
				}
			}
		},
		ADD_VISUALIZER(id: number) {
			this.visualizers.push(id);
		},
		REMOVE_VISUALIZER(id: number) {
			this.visualizers.splice(this.visualizers.indexOf(id), 1);
		},
		SET_PANEL_LAYOUT(payload: LayoutType) {
			const v: any = [];
			const extractVisualizers = (layout: LayoutType) => {
				if (layout.children != null) {
					for (let i = 0; i < layout.children.length; i++) {
						if (layout.children[i].component == "Visualizer") {
							v.push(layout.children[i].id);
							extractVisualizers(layout.children[i]);
						}
						if (layout.children[i].children != null) {
							extractVisualizers(layout.children[i]);
						}
					}
				}
			};

			extractVisualizers(payload);
			this.visualizers = v;
			this.panelLayout = payload;
			CleanLayout(this.panelLayout);
		},
		REPLACE_COMPONENT_BY_ID(layout: any, parent: any, component: any) {
			if (layout.children != null) {
				if (layout.id == parent.id) {
					return component;
				} else {
					for (let i = 0; i < layout.children.length; i++) {
						layout.children[i] = this.REPLACE_COMPONENT_BY_ID(
							layout.children[i],
							parent,
							component
						);
					}
				}
			}
			return layout;
		},
		async SPLIT_PANEL(payload: { panel1: number; type: string; clone: boolean }) {
			const PANEL1: number = payload.panel1;
			const LAYOUT: LayoutType = this.panelLayout;
			const P1_OBJ: LayoutType | void = FindNode(PANEL1, this.panelLayout.children);
			const COMP_DESTINATION_PARENT: LayoutType | void = RetrieveParentElementFromLayout(
				this.panelLayout,
				PANEL1
			);
			const COMP_DESTINATION_PARENT_PARENT: LayoutType | void =
				RetrieveParentElementFromLayout(
					this.panelLayout,
					Number(COMP_DESTINATION_PARENT!.id)
				);
			let newComp: any;
			const visID2 = GetNewId();
			let viewId: any = null;

			if (payload.type == "v" || payload.type == "h") {
				viewId = payload.clone
					? await pvw().PVW_CLONE_VIEW()
					: await pvw().PVW_ADD_VIEW({ viewType: "3D" });
			}

			if (payload.type == "v") {
				if (COMP_DESTINATION_PARENT_PARENT!.type == "column") {
					newComp = {
						id:
							COMP_DESTINATION_PARENT!.type != "root"
								? COMP_DESTINATION_PARENT!.id
								: GetNewId(),
						type: "row",
						componentState: {
							flex: 1,
							lastFlex: 1,
							color: ""
						},
						children: [
							{
								id: GetNewId(),
								componentName: "",
								component: "",
								componentState: { flex: 1, lastFlex: 1, color: "" },
								type: "stacked",
								children:
									COMP_DESTINATION_PARENT!.type == "stacked"
										? [...COMP_DESTINATION_PARENT!.children]
										: [{ ...P1_OBJ }]
							},
							{
								id: GetNewId(),
								componentName: "",
								component: "",
								componentState: { flex: 1, lastFlex: 1, color: "" },
								type: "stacked",
								children: [
									Object.assign(
										{ ...P1_OBJ },
										{
											id: visID2,
											componentState: { ...P1_OBJ!.componentState, viewId }
										}
									) as LayoutType
								]
							}
						]
					};
					this.ADD_VISUALIZER(visID2);

					this.panelLayout = this.REPLACE_COMPONENT_BY_ID(
						LAYOUT,
						COMP_DESTINATION_PARENT,
						newComp
					);
					CleanLayout(LAYOUT);
				} else {
					newComp = {
						id:
							COMP_DESTINATION_PARENT!.type != "root"
								? COMP_DESTINATION_PARENT!.id
								: GetNewId(),
						type: "row",
						componentState: {
							flex: 1,
							lastFlex: COMP_DESTINATION_PARENT!.componentState.flex,
							color: ""
						},
						children: [
							{
								id: GetNewId(),
								componentName: "",
								component: "",
								componentState: {
									flex: 1,
									lastFlex: COMP_DESTINATION_PARENT!.componentState.flex,
									color: ""
								},
								type: "stacked",
								children:
									COMP_DESTINATION_PARENT!.type == "stacked"
										? [...COMP_DESTINATION_PARENT!.children]
										: [{ ...P1_OBJ }]
							},
							{
								id: GetNewId(),
								componentName: "",
								component: "",
								componentState: {
									flex: 1,
									lastFlex: COMP_DESTINATION_PARENT!.componentState.flex,
									color: ""
								},
								type: "stacked",
								children: [
									Object.assign(
										{ ...P1_OBJ },
										{
											id: visID2,
											componentState: { ...P1_OBJ!.componentState, viewId }
										}
									) as LayoutType
								]
							}
						]
					};
					this.ADD_VISUALIZER(visID2);
					this.panelLayout = this.REPLACE_COMPONENT_BY_ID(
						this.panelLayout,
						COMP_DESTINATION_PARENT,
						newComp
					);
					CleanLayout(LAYOUT);
				}
			} else if (payload.type == "h") {
				newComp = {
					id:
						COMP_DESTINATION_PARENT!.type != "root"
							? COMP_DESTINATION_PARENT!.id
							: GetNewId(),
					type: "column",

					componentState: {
						flex: 1,
						lastFlex: COMP_DESTINATION_PARENT!.componentState.flex,
						color: ""
					},
					children: [
						{
							id: GetNewId(),
							componentName: "",
							component: "",
							componentState: { flex: 1, lastFlex: 1, color: "" },
							type: "stacked",
							children:
								COMP_DESTINATION_PARENT!.type == "stacked"
									? [...COMP_DESTINATION_PARENT!.children]
									: [{ ...P1_OBJ }]
						},
						{
							id: GetNewId(),
							componentName: "",
							component: "",
							componentState: { flex: 1, lastFlex: 1, color: "" },
							type: "stacked",
							children: [
								Object.assign(
									{ ...P1_OBJ },
									{
										id: visID2,
										componentState: { ...P1_OBJ!.componentState, viewId }
									}
								) as LayoutType
							]
						}
					]
				};

				this.ADD_VISUALIZER(visID2);
				this.panelLayout = this.REPLACE_COMPONENT_BY_ID(
					LAYOUT,
					COMP_DESTINATION_PARENT,
					newComp
				);

				CleanLayout(LAYOUT);
			}
			if (payload.type == "datav") {
				if (COMP_DESTINATION_PARENT_PARENT!.type == "column") {
					newComp = {
						id:
							COMP_DESTINATION_PARENT!.type != "root"
								? COMP_DESTINATION_PARENT!.id
								: GetNewId(),
						type: "row",
						componentState: {
							flex: 1,
							lastFlex: 1,
							color: ""
						},
						children: [
							{
								id: GetNewId(),
								componentName: "",
								component: "",
								componentState: { flex: 1, lastFlex: 1, color: "" },
								type: "stacked",
								children:
									COMP_DESTINATION_PARENT!.type == "stacked"
										? [...COMP_DESTINATION_PARENT!.children]
										: [{ ...P1_OBJ }]
							},
							{
								id: GetNewId(),
								componentName: "",
								component: "",
								componentState: { flex: 1, lastFlex: 1, color: "" },
								type: "stacked",
								children: [
									Object.assign(
										{ ...P1_OBJ },
										{
											id: visID2,
											componentName: "Data Table",
											component: "PickTable",
											type: "component",
											componentState: {
												...P1_OBJ!.componentState,
												viewId: visID2
											}
										}
									) as LayoutType
								]
							}
						]
					};
					this.ADD_VISUALIZER(visID2);

					this.panelLayout = this.REPLACE_COMPONENT_BY_ID(
						LAYOUT,
						COMP_DESTINATION_PARENT,
						newComp
					);
					CleanLayout(LAYOUT);
				} else {
					newComp = {
						id:
							COMP_DESTINATION_PARENT!.type != "root"
								? COMP_DESTINATION_PARENT!.id
								: GetNewId(),
						type: "row",
						componentState: {
							flex: 1,
							lastFlex: COMP_DESTINATION_PARENT!.componentState.flex,
							color: ""
						},
						children: [
							{
								id: GetNewId(),
								componentName: "",
								component: "",
								componentState: {
									flex: 1,
									lastFlex: COMP_DESTINATION_PARENT!.componentState.flex,
									color: ""
								},
								type: "stacked",
								children:
									COMP_DESTINATION_PARENT!.type == "stacked"
										? [...COMP_DESTINATION_PARENT!.children]
										: [{ ...P1_OBJ }]
							},
							{
								id: GetNewId(),
								componentName: "",
								component: "",
								componentState: {
									flex: 1,
									lastFlex: COMP_DESTINATION_PARENT!.componentState.flex,
									color: ""
								},
								type: "stacked",
								children: [
									Object.assign(
										{ ...P1_OBJ },
										{
											id: visID2,
											componentName: `Data Table`,
											component: "PickTable",
											type: "component",
											componentState: {
												...P1_OBJ!.componentState,
												viewId: visID2
											}
										}
									) as LayoutType
								]
							}
						]
					};
					this.ADD_VISUALIZER(visID2);
					this.panelLayout = this.REPLACE_COMPONENT_BY_ID(
						this.panelLayout,
						COMP_DESTINATION_PARENT,
						newComp
					);
					CleanLayout(LAYOUT);
				}
			} else if (payload.type == "datah") {
				newComp = {
					id:
						COMP_DESTINATION_PARENT!.type != "root"
							? COMP_DESTINATION_PARENT!.id
							: GetNewId(),
					type: "column",

					componentState: {
						flex: 1,
						lastFlex: COMP_DESTINATION_PARENT!.componentState.flex,
						color: ""
					},
					children: [
						{
							id: GetNewId(),
							componentName: "",
							component: "",
							componentState: { flex: 1, lastFlex: 1, color: "" },
							type: "stacked",
							children:
								COMP_DESTINATION_PARENT!.type == "stacked"
									? [...COMP_DESTINATION_PARENT!.children]
									: [{ ...P1_OBJ }]
						},
						{
							id: GetNewId(),
							componentName: "",
							component: "",
							componentState: { flex: 1, lastFlex: 1, color: "" },
							type: "stacked",
							children: [
								Object.assign(
									{ ...P1_OBJ },
									{
										id: visID2,
										componentName: "Data Table",
										type: "component",
										component: "PickTable",
										componentState: {
											...P1_OBJ!.componentState,
											viewId: visID2
										}
									}
								) as LayoutType
							]
						}
					]
				};

				this.ADD_VISUALIZER(visID2);
				this.panelLayout = this.REPLACE_COMPONENT_BY_ID(
					LAYOUT,
					COMP_DESTINATION_PARENT,
					newComp
				);

				CleanLayout(LAYOUT);
			}
		},

		RESIZE_PANEL(payload: { panel1: number; panel2: number; type: string }) {
			const PANEL1: number = payload.panel1;
			const PANEL2: number = payload.panel2;
			const TYPE: string = payload.type;
			const P1_EL: HTMLElement = document.getElementById(PANEL1.toString()) as HTMLElement;
			const P2_EL: HTMLElement = document.getElementById(PANEL2.toString()) as HTMLElement;
			const P1_OBJ: LayoutType | void = FindNode(PANEL1, this.panelLayout.children);
			const P2_OBJ: LayoutType | void = FindNode(PANEL2, this.panelLayout.children);
			if (P1_OBJ == null || P2_OBJ == null) return;
			const PANEL1_OFFSET = P1_EL.getBoundingClientRect();
			const PANEL2_OFFSET = P2_EL.getBoundingClientRect();
			const TOTAL_FLEX_SIZE: number = P1_OBJ.componentState.flex + P2_OBJ.componentState.flex;

			const TOTAL_WIDTH: number = PANEL1_OFFSET.width + PANEL2_OFFSET.width;
			const TOTAL_HEIGHT: number = PANEL1_OFFSET.height + PANEL2_OFFSET.height;

			const getAmountOfHorizontalChildren: any = (obj: any, count: number) => {
				if (obj.children && obj.children.length > 0) {
					for (let i = 0; i < obj.children.length; i++) {
						if (obj.children[i].type == "row") {
							count += getAmountOfHorizontalChildren(obj.children[i], count);
						}
						if (obj.children[i].type == "column") {
							count += 1;
						}
						if (obj.children[i].type == "stacked") {
							count += 1;
						}
					}
					return count;
				} else {
					return count;
				}
			};
			const getAmountOfVerticalChildren: any = (obj: any, count: number) => {
				if (obj.children && obj.children.length > 0) {
					for (let i = 0; i < obj.children.length; i++) {
						if (obj.children[i].type == "column") {
							// count += obj.children[i].children.length
							count += getAmountOfVerticalChildren(obj.children[i], count);
						}
						if (obj.children[i].type == "row") {
							count += 1;
						}
						if (obj.children[i].type == "stacked") {
							count += 1;
						}
					}
					return count;
				} else {
					return count;
				}
			};

			const child1Count = getAmountOfHorizontalChildren(P1_OBJ, 0);
			const child2Count = getAmountOfHorizontalChildren(P2_OBJ, 0);

			const child1CountV = getAmountOfVerticalChildren(P1_OBJ, 0);
			const child2CountV = getAmountOfVerticalChildren(P2_OBJ, 0);

			const MIN_WIDTH1: number =
				(TOTAL_FLEX_SIZE / TOTAL_WIDTH) * (P1_OBJ.type == "row" ? 250 * child1Count : 250);
			const MIN_WIDTH2: number =
				(TOTAL_FLEX_SIZE / TOTAL_WIDTH) * (P2_OBJ.type == "row" ? 250 * child2Count : 250);

			const MIN_HEIGHT1: number =
				(TOTAL_FLEX_SIZE / TOTAL_HEIGHT) *
				(P1_OBJ.type == "column" ? 250 * child1CountV : 250);
			const MIN_HEIGHT2: number =
				(TOTAL_FLEX_SIZE / TOTAL_HEIGHT) *
				(P2_OBJ.type == "column" ? 250 * child2CountV : 250);

			const NEW_PANEL1_OFFSET = P1_EL.getBoundingClientRect();
			const mousemoveListener = (e: MouseEvent) => {
				const X: number = (e.pageX - NEW_PANEL1_OFFSET.left) / TOTAL_WIDTH;
				const Y: number = (e.pageY - NEW_PANEL1_OFFSET.top) / TOTAL_HEIGHT;
				let increment = X;
				if (TYPE == "column") {
					increment = Y;
					if (
						TOTAL_FLEX_SIZE * increment > MIN_HEIGHT1 &&
						TOTAL_FLEX_SIZE * (1 - increment) > MIN_HEIGHT2
					) {
						UpdateFlex(this.panelLayout.children, PANEL1, TOTAL_FLEX_SIZE * increment);
						UpdateFlex(
							this.panelLayout.children,
							PANEL2,
							TOTAL_FLEX_SIZE * (1 - increment)
						);
					}
				} else {
					if (
						TOTAL_FLEX_SIZE * increment > MIN_WIDTH1 &&
						TOTAL_FLEX_SIZE * (1 - increment) > MIN_WIDTH2
					) {
						UpdateFlex(this.panelLayout.children, PANEL1, TOTAL_FLEX_SIZE * increment);
						UpdateFlex(
							this.panelLayout.children,
							PANEL2,
							TOTAL_FLEX_SIZE * (1 - increment)
						);
					}
				}
			};
			const mouseupListener = () => {
				window.removeEventListener("mousemove", mousemoveListener);
				window.removeEventListener("mouseup", mouseupListener);
			};
			window.addEventListener("mousemove", mousemoveListener);
			window.addEventListener("mouseup", mouseupListener);
		},
		REMOVE_COMPONENT(payload: { id: number }) {
			this.REMOVE_VISUALIZER(payload.id);
			const LAYOUT: LayoutType = this.panelLayout;
			LAYOUT.children = RemoveElementFromChildren(this.panelLayout.children, payload.id);
			CleanLayout(LAYOUT);
		},
		COLLAPSE_PANEL(payload: { type: string; id: number; id2: number }) {
			const LAYOUT: LayoutType = this.panelLayout;
			CollapsePanel(LAYOUT, payload.type, payload.id, payload.id2);
		},
		MOVE_COMPONENT(payload: { type: string; id: number; dropEl: number }) {
			const TYPE: string = payload.type;
			const COMP_TO_MOVE: LayoutType | void = RetrieveElementFromLayout(
				this.panelLayout,
				payload.id
			);
			const STATIC_COMP_TO_MOVE: any = { ...COMP_TO_MOVE };
			const COMP_DESTINATION: LayoutType | void = RetrieveElementFromLayout(
				this.panelLayout,
				payload.dropEl
			);
			const COMP_DESTINATION_PARENT: LayoutType | void = RetrieveParentElementFromLayout(
				this.panelLayout,
				Number(payload.dropEl)
			);
			const COMP_DESTINATION_PARENT2: LayoutType | void = RetrieveParentElementFromLayout(
				this.panelLayout,
				Number(payload.id)
			);
			if (
				COMP_DESTINATION_PARENT2 == null ||
				COMP_TO_MOVE == null ||
				COMP_DESTINATION == null ||
				COMP_DESTINATION_PARENT == null
			) {
				return;
			}
			// if(COMP_DESTINATION_PARENT2.id == payload.dropEl) return;
			const LAYOUT: LayoutType = this.panelLayout;
			if (TYPE == "stack") {
				COMP_TO_MOVE.id = GetNewId();
				LAYOUT.children = AddElementToStack(
					LAYOUT.children,
					payload.dropEl,
					STATIC_COMP_TO_MOVE
				);
				LAYOUT.children = RemoveElementFromChildren(LAYOUT.children, COMP_TO_MOVE.id);
				this.panelLayout = LAYOUT;
				return;
			}
			if (TYPE == "bottom" || TYPE == "top") {
				if (LAYOUT.children.length == 1) {
					if (COMP_DESTINATION_PARENT2.id == payload.dropEl) return;
				}
				const newComp: LayoutType = {
					id: GetNewId(),
					type: "column",
					componentName: "",
					component: "",
					componentState: {
						flex: 1,
						lastFlex: 1,
						color: ""
					},
					children: [
						{
							id: GetNewId(),
							componentName: "",
							component: "",
							componentState: { flex: 1, lastFlex: 1, color: "" },
							type: "stacked",
							children: [
								{
									...COMP_TO_MOVE,
									componentState: { ...COMP_TO_MOVE.componentState, flex: 1 },
									id: COMP_TO_MOVE.id
								}
							]
						},
						{
							...COMP_DESTINATION,
							componentState: { ...COMP_DESTINATION.componentState, flex: 1 },
							id: GetNewId()
						}
					]
				};
				if (TYPE == "bottom") {
					newComp.children.reverse();
				}
				LAYOUT.children = RemoveElementFromChildren(this.panelLayout.children, payload.id);
				LAYOUT.children = RemoveElementFromChildren(
					this.panelLayout.children,
					COMP_TO_MOVE.id
				);
				AddElementToRow(
					LAYOUT,
					COMP_DESTINATION_PARENT.id,
					COMP_DESTINATION.id,
					newComp,
					TYPE,
					this.panelLayout
				);
				LAYOUT.children = RemoveElementFromChildren(
					this.panelLayout.children,
					COMP_DESTINATION.id
				);
				CleanLayout(LAYOUT);
			}
			if (TYPE == "left" || TYPE == "right") {
				const PARENT_TYPE: string = COMP_DESTINATION_PARENT.type;
				COMP_TO_MOVE.id = GetNewId();
				let newComp: LayoutType = {
					id: GetNewId(),
					type: "stacked",
					componentName: "",
					component: "",
					componentState: {
						flex: 1,
						lastFlex: 1,
						color: ""
					},
					children: [STATIC_COMP_TO_MOVE]
				};
				if (PARENT_TYPE == "root") {
					LAYOUT.children = RemoveElementFromChildren(
						this.panelLayout.children,
						COMP_TO_MOVE.id
					);
					AddElementToRow(
						LAYOUT,
						COMP_DESTINATION_PARENT.id,
						COMP_DESTINATION.id,
						newComp,
						TYPE,
						this.panelLayout
					);
					CleanLayout(LAYOUT);
				}
				if (PARENT_TYPE == "row") {
					LAYOUT.children = RemoveElementFromChildren(
						this.panelLayout.children,
						COMP_TO_MOVE.id
					);
					AddElementToRow(
						LAYOUT,
						COMP_DESTINATION_PARENT.id,
						COMP_DESTINATION.id,
						newComp,
						TYPE,
						this.panelLayout
					);
					CleanLayout(LAYOUT);
				}
				if (PARENT_TYPE == "column" && COMP_DESTINATION.type == "stacked") {
					newComp = {
						id: GetNewId(),
						type: "row",
						componentName: "",
						component: "",
						componentState: {
							flex: 1,
							lastFlex: 1,
							color: ""
						},
						children: [
							{
								id: GetNewId(),
								componentName: "",
								component: "",
								componentState: { flex: 1, color: "#fff", lastFlex: 1 },
								type: "stacked",
								children: [
									{
										...COMP_TO_MOVE,
										componentState: { ...COMP_TO_MOVE.componentState, flex: 1 },
										id: STATIC_COMP_TO_MOVE.id
									}
								]
							},
							{
								...COMP_DESTINATION,
								componentState: { ...COMP_TO_MOVE.componentState, flex: 1 },
								id: GetNewId()
							}
						]
					};
					if (TYPE == "right") {
						newComp.children.reverse();
					}

					AddElementToRow(
						LAYOUT,
						COMP_DESTINATION_PARENT.id,
						COMP_DESTINATION.id,
						newComp,
						TYPE,
						this.panelLayout
					);
					LAYOUT.children = RemoveElementFromChildren(
						this.panelLayout.children,
						COMP_TO_MOVE.id
					);
					LAYOUT.children = RemoveElementFromChildren(
						this.panelLayout.children,
						COMP_DESTINATION.id
					);
					CleanLayout(LAYOUT);
				}
			}

			// CleanLayout(LAYOUT)
		}
	}
});
